import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Lock, Loader2 } from "lucide-react";

const resetPasswordSchema = z.object({
  password: z.string().min(6, { message: "Le mot de passe doit contenir au moins 6 caractères" }),
  confirmPassword: z.string().min(1, { message: "Veuillez confirmer votre mot de passe" }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    let sessionEstablished = false;

    // Écouter les changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Token de récupération traité, session établie
        sessionEstablished = true;
        setSessionReady(true);
        setCheckingSession(false);
      } else if (event === 'SIGNED_IN' && session) {
        // Session existante (fallback)
        sessionEstablished = true;
        setSessionReady(true);
        setCheckingSession(false);
      }
    });

    // Vérifier aussi s'il y a déjà une session (cas où l'événement a déjà été traité)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        sessionEstablished = true;
        setSessionReady(true);
      }
      setCheckingSession(false);
    });

    // Timeout de sécurité (10 secondes)
    const timeout = setTimeout(() => {
      if (!sessionEstablished) {
        toast({
          title: "Lien invalide",
          description: "Ce lien de réinitialisation est invalide ou a expiré.",
          variant: "destructive",
        });
        navigate("/auth");
      }
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate, toast]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validatedData = resetPasswordSchema.parse({ password, confirmPassword });

      const { error } = await supabase.auth.updateUser({
        password: validatedData.password,
      });

      if (error) {
        toast({
          title: "Erreur",
          description: "Une erreur s'est produite lors de la réinitialisation de votre mot de passe.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Mot de passe modifié !",
          description: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.",
        });
        navigate("/auth");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Erreur de validation",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-secondary/20 to-accent/10">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          {/* Branding Section */}
          <div className="text-center space-y-3">
            <img
              src="https://www.terre-adelice.eu/wp-content/uploads/2025/10/logo-v-b.3-t.png"
              alt="Terre adélice"
              className="h-24 mx-auto object-contain"
            />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-fade-in">
              Fidélice
            </h1>
          </div>

          {/* Reset Password Card */}
          <Card className="w-full shadow-[var(--shadow-medium)]">
            <CardHeader className="space-y-0.5 pb-4 text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-xl font-bold">
                Nouveau mot de passe
              </CardTitle>
              <CardDescription className="text-sm">
                Choisissez un nouveau mot de passe sécurisé
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-3">
              {checkingSession || !sessionReady ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Vérification du lien...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm font-semibold">
                      Nouveau mot de passe
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-sm font-semibold">
                      Confirmer le mot de passe
                    </Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-semibold mt-4"
                    disabled={loading}
                  >
                    {loading ? "Modification..." : "Modifier mon mot de passe"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
